import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CodeEditor from '@uiw/react-textarea-code-editor';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Share2, 
  Copy, 
  Download, 
  Lock, 
  Unlock, 
  Clock, 
  FileCode,
  Trash2,
  Eye,
  Upload,
  FileText,
  Type,
  Code,
  Image as ImageIcon,
  FileDown,
  History as HistoryIcon,
  Star,
  ChevronDown,
  ArrowLeft,
  Maximize,
  Minimize,
  Printer,
  QrCode,
  X,
  Twitter,
  Facebook,
  Linkedin,
  Mail,
  BookOpen,
  Flame,
  Hash,
  Code2,
  Search,
  Replace
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { languages, expirationOptions, getFileExtension } from '../utils/languages';
import { encodeData, generateId, getExpirationDate, hashPassword } from '../utils/storage';
import { readFileAsText, readFileAsDataURL, detectLanguageFromFile, validateFileSize, formatFileSize } from '../utils/fileImport';
import { exportToPDF, exportToHTML } from '../utils/export';
import { addToHistory, addToFavorites } from '../utils/history';
import { createPaste, updatePaste, checkApiHealth } from '../utils/api';
import RichTextEditor from './RichTextEditor';
import History from './History';

// Auto-save draft to localStorage
const DRAFT_KEY = 'scrawl_draft';

const Editor = ({ theme }) => {
  const navigate = useNavigate();
  const { getToken, isAuthenticated } = useAuth();
  const [content, setContent] = useState('');
  const [richContent, setRichContent] = useState('');
  const [title, setTitle] = useState('');
  const [language, setLanguage] = useState('plaintext');
  const [expiration, setExpiration] = useState('10min');
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [editorMode, setEditorMode] = useState('code'); // 'code' or 'rich'
  const [images, setImages] = useState([]);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const [burnAfterRead, setBurnAfterRead] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [showConfirmPublish, setShowConfirmPublish] = useState(false);
  const [customSlug, setCustomSlug] = useState('');
  const [slugError, setSlugError] = useState('');
  const [editingShortId, setEditingShortId] = useState(null); // Track if editing existing paste
  
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const editorContainerRef = useRef(null);

  // Check for edit data first (from PublishedViewer), then load draft
  useEffect(() => {
    const editData = sessionStorage.getItem('scrawl_edit_paste');
    
    if (editData) {
      // Load edit data from PublishedViewer
      try {
        const data = JSON.parse(editData);
        
        // Set all fields
        setTitle(data.title || '');
        setLanguage(data.language || 'plaintext');
        setEditorMode(data.editorMode || 'code');
        setImages(data.images || []);
        
        // Track that we're editing an existing paste
        if (data.originalShortId) {
          setEditingShortId(data.originalShortId);
        }
        
        // Set content based on editor mode
        if (data.editorMode === 'rich') {
          setRichContent(data.content || '');
          setContent(''); // Clear code content
        } else {
          setContent(data.content || '');
          setRichContent(''); // Clear rich content
        }
        
        // Clear the edit data after loading
        sessionStorage.removeItem('scrawl_edit_paste');
        
        // Clear any existing draft to avoid conflicts
        localStorage.removeItem(DRAFT_KEY);
        
        toast.success('Editing paste - same URL will be kept!', {
          style: {
            background: '#0f3460',
            color: '#eaeaea',
            border: '3px solid #0eff9f',
            fontFamily: 'JetBrains Mono, monospace',
            fontWeight: '600'
          }
        });
      } catch (e) {
        console.error('Failed to load edit data:', e);
        sessionStorage.removeItem('scrawl_edit_paste');
      }
    } else {
      // Load draft on mount (only if no edit data)
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          if (draft.content) setContent(draft.content);
          if (draft.richContent) setRichContent(draft.richContent);
          if (draft.title) setTitle(draft.title);
          if (draft.language) setLanguage(draft.language);
          if (draft.editorMode) setEditorMode(draft.editorMode);
          if (draft.images) setImages(draft.images);
          setLastSaved(new Date(draft.savedAt));
          toast.success('Draft restored');
        } catch (e) {
          console.error('Failed to load draft:', e);
        }
      }
    }
  }, []);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    const currentContent = editorMode === 'code' ? content : richContent;
    if (!currentContent && !title) return;

    const saveTimer = setTimeout(() => {
      const draft = {
        content,
        richContent,
        title,
        language,
        editorMode,
        images,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      setLastSaved(new Date());
    }, 30000);

    return () => clearTimeout(saveTimer);
  }, [content, richContent, title, language, editorMode, images]);

  // Handle fullscreen toggle
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+S or Cmd+S - Save draft
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSaveDraft();
      }
      // Ctrl+Enter or Cmd+Enter - Publish
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handlePublish();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [content, richContent, editorMode, title]);

  // Validate custom slug
  const validateSlug = (slug) => {
    if (!slug) {
      setSlugError('');
      return true;
    }
    if (slug.length < 3) {
      setSlugError('Slug must be at least 3 characters');
      return false;
    }
    if (slug.length > 30) {
      setSlugError('Slug must be 30 characters or less');
      return false;
    }
    if (!/^[a-zA-Z0-9-_]+$/.test(slug)) {
      setSlugError('Only letters, numbers, hyphens, and underscores allowed');
      return false;
    }
    setSlugError('');
    return true;
  };

  const handleSlugChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/\s+/g, '-');
    setCustomSlug(value);
    validateSlug(value);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate reading time
  const getReadingTime = () => {
    const currentContent = editorMode === 'code' ? content : richContent;
    const text = currentContent.replace(/<[^>]*>/g, ''); // Strip HTML tags
    const words = text.trim().split(/\s+/).filter(w => w.length > 0).length;
    const minutes = Math.ceil(words / 200); // Average reading speed
    return minutes < 1 ? '< 1 min' : `${minutes} min read`;
  };

  // Calculate word count
  const getWordCount = () => {
    const currentContent = editorMode === 'code' ? content : richContent;
    const text = currentContent.replace(/<[^>]*>/g, ''); // Strip HTML tags
    return text.trim().split(/\s+/).filter(w => w.length > 0).length;
  };

  // Generate embed code
  const getEmbedCode = () => {
    if (!shareUrl) return '';
    return `<iframe src="${shareUrl}" width="100%" height="500" frameborder="0" style="border: 4px solid #000; border-radius: 0;"></iframe>`;
  };

  // Toggle fullscreen
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await editorContainerRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (e) {
      console.error('Fullscreen error:', e);
    }
  };

  // Print content
  const handlePrint = () => {
    const currentContent = editorMode === 'code' ? content : richContent;
    if (!currentContent) {
      toast.error('Nothing to print');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title || 'Scrawl'}</title>
          <style>
            body { font-family: 'Segoe UI', sans-serif; padding: 2rem; max-width: 800px; margin: 0 auto; }
            pre { background: #f5f5f5; padding: 1rem; overflow-x: auto; }
            h1 { border-bottom: 2px solid #333; padding-bottom: 0.5rem; }
          </style>
        </head>
        <body>
          <h1>${title || 'Untitled'}</h1>
          ${editorMode === 'code' ? `<pre><code>${currentContent.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>` : currentContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Social sharing
  const shareToSocial = (platform) => {
    if (!shareUrl) return;
    
    const text = encodeURIComponent(title || 'Check out my paste on Scrawl');
    const url = encodeURIComponent(shareUrl);
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      email: `mailto:?subject=${text}&body=${url}`
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  // Save draft manually
  const saveDraft = () => {
    const draft = {
      content,
      richContent,
      title,
      language,
      editorMode,
      images,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    setLastSaved(new Date());
    toast.success('Draft saved');
  };

  // Clear draft
  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setLastSaved(null);
  };

  // Show confirmation modal before publishing
  const handlePublish = () => {
    const currentContent = editorMode === 'code' ? content : richContent;
    
    if (!currentContent.trim()) {
      toast.error('Please enter some content to publish');
      return;
    }

    // Show confirmation modal
    setShowConfirmPublish(true);
  };

  // Actually publish after confirmation
  const handleConfirmPublish = async () => {
    const currentContent = editorMode === 'code' ? content : richContent;

    try {
      setShowConfirmPublish(false);
      toast.loading(editingShortId ? 'Updating...' : 'Publishing...');
      
      const pasteData = {
        title: title || 'Untitled',
        content: currentContent,
        language: editorMode === 'code' ? language : 'html',
        editorMode,
        images,
      };

      let result;
      
      if (editingShortId) {
        // Update existing paste (keep same URL)
        result = await updatePaste(editingShortId, pasteData);
      } else {
        // Create new paste
        pasteData.expiresAt = getExpirationDate(expiration);
        pasteData.passwordHash = isPasswordProtected && password ? await hashPassword(password) : null;
        pasteData.burnAfterRead = burnAfterRead;
        pasteData.customSlug = customSlug.trim() || null;
        result = await createPaste(pasteData);
      }
      
      // Create short URL
      const newShareUrl = `${window.location.origin}/#/p/${result.shortId}`;
      
      // Mark this paste as just created by this user (for burn-after-read)
      // This prevents the author's first view from counting
      sessionStorage.setItem(`author_${result.shortId}`, 'true');
      
      // Also save to local history
      await addToHistory({
        ...pasteData,
        id: result.shortId,
        createdAt: new Date().toISOString(),
        isPublished: true,
      });

      setShareUrl(newShareUrl);
      clearDraft();
      setEditingShortId(null); // Clear editing state
      toast.dismiss();
      toast.success(editingShortId ? 'Updated successfully!' : 'Published successfully!');
      
      // Navigate to the published paste
      navigate(`/p/${result.shortId}`);
    } catch (error) {
      toast.dismiss();
      toast.error(error.message || 'Failed to publish. Is the server running?');
      console.error(error);
    }
  };

  // Cancel publish
  const handleCancelPublish = () => {
    setShowConfirmPublish(false);
  };

  // Share locally (URL-encoded, no server needed)
  const handleShare = async () => {
    const currentContent = editorMode === 'code' ? content : richContent;
    
    if (!currentContent.trim()) {
      toast.error('Please enter some content to share');
      return;
    }

    try {
      const pasteData = {
        id: generateId(),
        title: title || 'Untitled',
        content: currentContent,
        language: editorMode === 'code' ? language : 'html',
        editorMode,
        images,
        createdAt: new Date().toISOString(),
        expiresAt: getExpirationDate(expiration),
        passwordHash: isPasswordProtected && password ? await hashPassword(password) : null,
        burnAfterRead: burnAfterRead,
        viewCount: 0,
      };

      // Save to history
      await addToHistory(pasteData);

      const encoded = encodeData(pasteData);
      const newShareUrl = `${window.location.origin}/#/view/${encoded}`;
      
      setShareUrl(newShareUrl);
      clearDraft();
      toast.success('Share link created!');
      setShowQRModal(true);
    } catch (error) {
      toast.error('Failed to create share link');
      console.error(error);
    }
  };

  const handleCopy = async () => {
    const currentContent = editorMode === 'code' ? content : richContent;
    if (!currentContent) {
      toast.error('Nothing to copy');
      return;
    }
    await navigator.clipboard.writeText(currentContent);
    toast.success('Content copied to clipboard!');
  };

  const handleDownload = () => {
    const currentContent = editorMode === 'code' ? content : richContent;
    if (!currentContent) {
      toast.error('Nothing to download');
      return;
    }
    
    const blob = new Blob([currentContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'scrawl'}.${editorMode === 'code' ? getFileExtension(language) : 'html'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('File downloaded!');
  };

  const handleExportPDF = async () => {
    const currentContent = editorMode === 'code' ? content : richContent;
    if (!currentContent) {
      toast.error('Nothing to export');
      return;
    }
    
    try {
      toast.loading('Generating PDF...');
      await exportToPDF(currentContent, title, { language, theme });
      toast.dismiss();
      toast.success('PDF exported successfully!');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export PDF');
    }
    setShowExportMenu(false);
  };

  const handleExportHTML = () => {
    const currentContent = editorMode === 'code' ? content : richContent;
    if (!currentContent) {
      toast.error('Nothing to export');
      return;
    }
    
    exportToHTML(currentContent, title, { language, theme });
    toast.success('HTML file downloaded!');
    setShowExportMenu(false);
  };

  const handleClear = () => {
    setContent('');
    setRichContent('');
    setTitle('');
    setLanguage('plaintext');
    setExpiration('10min');
    setIsPasswordProtected(false);
    setPassword('');
    setImages([]);
    setCustomSlug('');
    setSlugError('');
    clearDraft();
    toast.success('Editor cleared!');
  };

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!validateFileSize(file, 5)) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    try {
      const text = await readFileAsText(file);
      const detectedLang = detectLanguageFromFile(file.name);
      
      setContent(text);
      setLanguage(detectedLang);
      setTitle(file.name.replace(/\.[^/.]+$/, ''));
      setEditorMode('code');
      
      toast.success(`Imported ${file.name}`);
    } catch (error) {
      toast.error('Failed to import file');
    }
    
    e.target.value = '';
  };

  // Image limits
  const MAX_IMAGES = 5;
  const MAX_TOTAL_IMAGE_SIZE_MB = 8;

  const calculateTotalImageSize = (imagesList) => {
    return imagesList.reduce((total, img) => {
      // Base64 data URL size (approximate original file size)
      const base64Length = img.url.length - (img.url.indexOf(',') + 1);
      const sizeInBytes = (base64Length * 3) / 4;
      return total + sizeInBytes;
    }, 0);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }

      // Check max images limit
      if (images.length >= MAX_IMAGES) {
        toast.error(`Maximum ${MAX_IMAGES} images allowed per paste`);
        break;
      }
      
      if (!validateFileSize(file, 2)) {
        toast.error(`${file.name} is too large. Maximum size is 2MB.`);
        continue;
      }

      // Check total size limit
      const currentTotalSize = calculateTotalImageSize(images);
      if ((currentTotalSize + file.size) > MAX_TOTAL_IMAGE_SIZE_MB * 1024 * 1024) {
        toast.error(`Total image size exceeds ${MAX_TOTAL_IMAGE_SIZE_MB}MB limit`);
        break;
      }

      try {
        const dataUrl = await readFileAsDataURL(file);
        setImages(prev => [...prev, { 
          id: generateId(), 
          name: file.name, 
          url: dataUrl,
          size: formatFileSize(file.size)
        }]);
        toast.success(`Added ${file.name}`);
      } catch (error) {
        toast.error(`Failed to load ${file.name}`);
      }
    }
    
    e.target.value = '';
  };

  const handleRemoveImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
    toast.success('Image removed');
  };

  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          // Check max images limit
          if (images.length >= MAX_IMAGES) {
            toast.error(`Maximum ${MAX_IMAGES} images allowed per paste`);
            return;
          }

          if (!validateFileSize(file, 2)) {
            toast.error('Image too large. Maximum size is 2MB.');
            return;
          }

          // Check total size limit
          const currentTotalSize = calculateTotalImageSize(images);
          if ((currentTotalSize + file.size) > MAX_TOTAL_IMAGE_SIZE_MB * 1024 * 1024) {
            toast.error(`Total image size exceeds ${MAX_TOTAL_IMAGE_SIZE_MB}MB limit`);
            return;
          }

          const dataUrl = await readFileAsDataURL(file);
          setImages(prev => [...prev, { 
            id: generateId(), 
            name: 'Pasted image', 
            url: dataUrl,
            size: formatFileSize(file.size)
          }]);
          toast.success('Image pasted');
        }
      }
    }
  }, [images]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    let currentImageCount = images.length;
    let currentTotalSize = calculateTotalImageSize(images);
    
    for (const file of files) {
      if (file.type.startsWith('image/')) {
        // Check max images limit
        if (currentImageCount >= MAX_IMAGES) {
          toast.error(`Maximum ${MAX_IMAGES} images allowed per paste`);
          break;
        }

        if (!validateFileSize(file, 2)) {
          toast.error(`${file.name} is too large. Maximum size is 2MB.`);
          continue;
        }

        // Check total size limit
        if ((currentTotalSize + file.size) > MAX_TOTAL_IMAGE_SIZE_MB * 1024 * 1024) {
          toast.error(`Total image size exceeds ${MAX_TOTAL_IMAGE_SIZE_MB}MB limit`);
          break;
        }

        const dataUrl = await readFileAsDataURL(file);
        setImages(prev => [...prev, { 
          id: generateId(), 
          name: file.name, 
          url: dataUrl,
          size: formatFileSize(file.size)
        }]);
        currentImageCount++;
        currentTotalSize += file.size;
        toast.success(`Added ${file.name}`);
      } else {
        if (!validateFileSize(file, 5)) {
          toast.error(`${file.name} is too large. Maximum size is 5MB.`);
          continue;
        }
        try {
          const text = await readFileAsText(file);
          const detectedLang = detectLanguageFromFile(file.name);
          setContent(text);
          setLanguage(detectedLang);
          setTitle(file.name.replace(/\.[^/.]+$/, ''));
          setEditorMode('code');
          toast.success(`Imported ${file.name}`);
        } catch (error) {
          toast.error(`Failed to import ${file.name}`);
        }
      }
    }
  };

  const handleLoadFromHistory = (paste) => {
    setTitle(paste.title || '');
    setLanguage(paste.language || 'plaintext');
    
    if (paste.editorMode === 'rich') {
      setEditorMode('rich');
      setRichContent(paste.content);
    } else {
      setEditorMode('code');
      setContent(paste.content);
    }
    
    if (paste.images) {
      setImages(paste.images);
    }
    
    setShowHistory(false);
    toast.success('Paste loaded');
  };

  const handleSaveToFavorites = async () => {
    const currentContent = editorMode === 'code' ? content : richContent;
    if (!currentContent.trim()) {
      toast.error('Nothing to save');
      return;
    }

    const pasteData = {
      id: generateId(),
      title: title || 'Untitled',
      content: currentContent,
      language: editorMode === 'code' ? language : 'html',
      editorMode,
      images,
      createdAt: new Date().toISOString(),
    };

    await addToFavorites(pasteData);
    toast.success('Saved to favorites!');
  };

  const currentContent = editorMode === 'code' ? content : richContent;
  const lineCount = currentContent.split('\n').length;
  const charCount = currentContent.length;

  if (showHistory) {
    return (
      <div className="editor-container">
        <div className="editor-toolbar">
          <button 
            type="button"
            className="action-btn secondary"
            onClick={() => setShowHistory(false)}
          >
            <ArrowLeft size={16} />
            Back to Editor
          </button>
        </div>
        <History theme={theme} onLoadPaste={handleLoadFromHistory} />
      </div>
    );
  }

  return (
    <div 
      ref={editorContainerRef}
      className={`editor-container ${isDragging ? 'dragging' : ''} ${isFullscreen ? 'fullscreen' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onPaste={handlePaste}
    >
      {isDragging && (
        <div className="drag-overlay">
          <Upload size={48} />
          <p>Drop files here</p>
        </div>
      )}

      <div className="editor-toolbar">
        <div className="toolbar-left">
          <input
            type="text"
            placeholder="Untitled paste..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="title-input"
          />
          <div className="slug-input-group">
            <span className="slug-prefix">/p/</span>
            <input
              type="text"
              placeholder="custom-url (optional)"
              value={customSlug}
              onChange={handleSlugChange}
              className={`slug-input ${slugError ? 'error' : ''}`}
              maxLength={30}
            />
            {slugError && <span className="slug-error">{slugError}</span>}
          </div>
        </div>
        
        <div className="toolbar-right">
          {/* Editor Mode Toggle */}
          <div className="mode-toggle">
            <button
              type="button"
              className={`mode-btn ${editorMode === 'code' ? 'active' : ''}`}
              onClick={() => setEditorMode('code')}
              title="Code Editor"
            >
              <Code size={16} />
              Code
            </button>
            <button
              type="button"
              className={`mode-btn ${editorMode === 'rich' ? 'active' : ''}`}
              onClick={() => setEditorMode('rich')}
              title="Rich Text Editor"
            >
              <Type size={16} />
              Rich Text
            </button>
          </div>

          {editorMode === 'code' && (
            <div className="toolbar-group">
              <FileCode size={16} />
              <select 
                value={language} 
                onChange={(e) => setLanguage(e.target.value)}
                className="toolbar-select"
              >
                {languages.map((lang) => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="toolbar-group">
            <Clock size={16} />
            <select 
              value={expiration} 
              onChange={(e) => setExpiration(e.target.value)}
              className="toolbar-select"
            >
              {expirationOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <button 
            type="button"
            className={`toolbar-btn ${isPasswordProtected ? 'active' : ''}`}
            onClick={() => setIsPasswordProtected(!isPasswordProtected)}
            title={isPasswordProtected ? 'Remove password' : 'Add password'}
          >
            {isPasswordProtected ? <Lock size={16} /> : <Unlock size={16} />}
          </button>

          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Import file"
          >
            <Upload size={16} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileImport}
            style={{ display: 'none' }}
            accept=".txt,.md,.js,.jsx,.ts,.tsx,.py,.java,.c,.cpp,.html,.css,.json,.xml,.yaml,.yml,.sql,.sh"
          />

          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => imageInputRef.current?.click()}
            title="Add images"
          >
            <ImageIcon size={16} />
          </button>
          <input
            ref={imageInputRef}
            type="file"
            multiple
            onChange={handleImageUpload}
            style={{ display: 'none' }}
            accept="image/*"
          />

          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => setShowHistory(true)}
            title="History & Favorites"
          >
            <HistoryIcon size={16} />
          </button>

          {editorMode === 'code' && (
            <button 
              type="button"
              className={`toolbar-btn ${showFindReplace ? 'active' : ''}`}
              onClick={() => setShowFindReplace(!showFindReplace)}
              title="Find & Replace"
            >
              <Search size={16} />
            </button>
          )}
        </div>
      </div>

      {isPasswordProtected && (
        <div className="password-bar">
          <Lock size={16} />
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter password for this paste..."
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="password-input"
          />
          <button 
            type="button"
            className="show-password-btn"
            onClick={() => setShowPassword(!showPassword)}
          >
            <Eye size={16} />
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
      )}

      {/* Burn After Read Option */}
      <div className="options-bar">
        <label className="checkbox-option">
          <input
            type="checkbox"
            checked={burnAfterRead}
            onChange={(e) => setBurnAfterRead(e.target.checked)}
          />
          <Flame size={16} className={burnAfterRead ? 'burn-active' : ''} />
          <span>Burn after reading</span>
          <span className="option-hint">(Self-destruct after first view)</span>
        </label>
      </div>

      {/* Images Preview */}
      {images.length > 0 && (
        <div className="images-bar">
          <div className="images-header">
            <ImageIcon size={16} />
            <span>Attached Images ({images.length})</span>
          </div>
          <div className="images-list">
            {images.map((img) => (
              <div key={img.id} className="image-preview">
                <img src={img.url} alt={img.name} />
                <div className="image-info">
                  <span className="image-name">{img.name}</span>
                  <span className="image-size">{img.size}</span>
                </div>
                <button 
                  type="button"
                  className="remove-image-btn"
                  onClick={() => handleRemoveImage(img.id)}
                  title="Remove image"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Find & Replace Bar (Code Editor) */}
      {showFindReplace && editorMode === 'code' && (
        <div className="find-replace-bar">
          <Search size={16} />
          <input
            type="text"
            placeholder="Find..."
            value={findText}
            onChange={(e) => setFindText(e.target.value)}
          />
          <Replace size={16} />
          <input
            type="text"
            placeholder="Replace with..."
            value={replaceText}
            onChange={(e) => setReplaceText(e.target.value)}
          />
          <button 
            type="button"
            onClick={() => {
              if (findText && content.includes(findText)) {
                setContent(content.replace(findText, replaceText));
                toast.success('Replaced first occurrence');
              } else {
                toast.error('Text not found');
              }
            }}
          >
            Replace
          </button>
          <button 
            type="button"
            onClick={() => {
              if (findText && content.includes(findText)) {
                setContent(content.replaceAll(findText, replaceText));
                toast.success('Replaced all occurrences');
              } else {
                toast.error('Text not found');
              }
            }}
          >
            Replace All
          </button>
          <button 
            type="button" 
            className="close-btn"
            onClick={() => setShowFindReplace(false)}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div className="editor-wrapper">
        {editorMode === 'code' ? (
          <>
            <div className="line-numbers">
              {Array.from({ length: Math.max(lineCount, 20) }, (_, i) => (
                <span key={i + 1}>{i + 1}</span>
              ))}
            </div>
            <CodeEditor
              value={content}
              language={language}
              placeholder="Paste your code or text here... (You can also drag & drop files or paste images)"
              onChange={(e) => setContent(e.target.value)}
              padding={16}
              data-color-mode={theme}
              style={{
                flex: 1,
                fontSize: 14,
                fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
                minHeight: '400px',
                backgroundColor: 'transparent',
              }}
            />
          </>
        ) : (
          <RichTextEditor 
            content={richContent} 
            onChange={setRichContent} 
            theme={theme}
            showFindReplace={showFindReplace}
            onToggleFindReplace={() => setShowFindReplace(!showFindReplace)}
          />
        )}
      </div>

      <div className="editor-footer">
        <div className="stats">
          <span>{lineCount} lines</span>
          <span>{charCount} characters</span>
          <span>{getWordCount()} words</span>
          <span><BookOpen size={12} /> {getReadingTime()}</span>
          {images.length > 0 && <span>{images.length} images</span>}
          {lastSaved && (
            <span className="auto-save-indicator">
              Auto-saved {lastSaved.toLocaleTimeString()}
            </span>
          )}
        </div>

        <div className="actions">
          <button type="button" className="action-btn secondary icon-only" onClick={toggleFullscreen} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
          </button>
          <button type="button" className="action-btn secondary icon-only" onClick={handlePrint} title="Print">
            <Printer size={16} />
          </button>
          <button type="button" className="action-btn secondary icon-only" onClick={saveDraft} title="Save Draft">
            <Download size={16} />
          </button>
          <button type="button" className="action-btn secondary" onClick={handleClear}>
            <Trash2 size={16} />
            Clear
          </button>
          <button type="button" className="action-btn secondary" onClick={handleSaveToFavorites}>
            <Star size={16} />
            Save
          </button>
          <button type="button" className="action-btn secondary" onClick={handleCopy}>
            <Copy size={16} />
            Copy
          </button>
          
          {/* Export Dropdown */}
          <div className="dropdown" ref={dropdownRef}>
            <button 
              type="button"
              className="action-btn secondary"
              onClick={() => setShowExportMenu(!showExportMenu)}
            >
              <FileDown size={16} />
              Export
              <ChevronDown size={14} className={showExportMenu ? 'rotate' : ''} />
            </button>
            {showExportMenu && (
              <div className="dropdown-menu">
                <button type="button" onClick={handleDownload}>
                  <FileText size={16} />
                  Download as File
                </button>
                <button type="button" onClick={handleExportPDF}>
                  <FileDown size={16} />
                  Export as PDF
                </button>
                <button type="button" onClick={handleExportHTML}>
                  <Code size={16} />
                  Export as HTML
                </button>
              </div>
            )}
          </div>

          <button type="button" className="action-btn primary" onClick={handleShare}>
            <Share2 size={16} />
            Share (Local)
          </button>
          
          <button type="button" className="action-btn publish" onClick={handlePublish}>
            <Upload size={16} />
            {editingShortId ? 'Update' : 'Publish'}
          </button>
        </div>
      </div>

      {/* QR Code & Social Share Modal */}
      {showQRModal && shareUrl && (
        <div className="modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="modal-content qr-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setShowQRModal(false)}>
              <X size={20} />
            </button>
            <h2>Share Your Paste</h2>
            {burnAfterRead && (
              <div className="burn-warning">
                <Flame size={16} />
                <span>This paste will self-destruct after first view!</span>
              </div>
            )}
            {shareUrl.length <= 2000 ? (
              <div className="qr-code-container">
                <QRCodeSVG value={shareUrl} size={200} level="L" />
              </div>
            ) : (
              <div className="qr-unavailable">
                <QrCode size={48} />
                <p>QR code unavailable for large pastes</p>
              </div>
            )}
            <p className="share-url">{shareUrl.length > 100 ? shareUrl.substring(0, 100) + '...' : shareUrl}</p>
            <div className="modal-buttons-row">
              <button 
                type="button" 
                className="action-btn secondary copy-url-btn"
                onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('Link copied!'); }}
              >
                <Copy size={16} />
                Copy Link
              </button>
              <button 
                type="button" 
                className="action-btn secondary"
                onClick={() => { setShowQRModal(false); setShowEmbedModal(true); }}
              >
                <Code2 size={16} />
                Embed
              </button>
            </div>
            <div className="social-share-buttons">
              <button type="button" className="social-btn twitter" onClick={() => shareToSocial('twitter')} title="Share on Twitter">
                <Twitter size={20} />
              </button>
              <button type="button" className="social-btn facebook" onClick={() => shareToSocial('facebook')} title="Share on Facebook">
                <Facebook size={20} />
              </button>
              <button type="button" className="social-btn linkedin" onClick={() => shareToSocial('linkedin')} title="Share on LinkedIn">
                <Linkedin size={20} />
              </button>
              <button type="button" className="social-btn email" onClick={() => shareToSocial('email')} title="Share via Email">
                <Mail size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Embed Code Modal */}
      {showEmbedModal && shareUrl && (
        <div className="modal-overlay" onClick={() => setShowEmbedModal(false)}>
          <div className="modal-content embed-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setShowEmbedModal(false)}>
              <X size={20} />
            </button>
            <h2><Code2 size={20} /> Embed Code</h2>
            <p className="embed-description">Copy this code to embed your paste in any website:</p>
            <div className="embed-code-container">
              <code>{getEmbedCode()}</code>
            </div>
            <button 
              type="button" 
              className="action-btn primary"
              onClick={() => { navigator.clipboard.writeText(getEmbedCode()); toast.success('Embed code copied!'); }}
            >
              <Copy size={16} />
              Copy Embed Code
            </button>
          </div>
        </div>
      )}

      {/* Confirm Publish Modal */}
      {showConfirmPublish && (
        <div className="modal-overlay" onClick={handleCancelPublish}>
          <div className="modal-content captcha-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={handleCancelPublish}>
              <X size={20} />
            </button>
            <h2>{editingShortId ? 'Update Paste' : 'Publish Paste'}</h2>
            <p className="captcha-description">
              {editingShortId 
                ? 'Are you sure you want to update this paste? The URL will remain the same.'
                : 'Are you sure you want to publish this paste?'
              }
            </p>
            
            {burnAfterRead && !editingShortId && (
              <div className="publish-warning">
                <Flame size={16} />
                <span>This paste will be deleted after being viewed once.</span>
              </div>
            )}
            
            <div className="captcha-actions">
              <button 
                type="button" 
                className="action-btn secondary"
                onClick={handleCancelPublish}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="action-btn publish"
                onClick={handleConfirmPublish}
              >
                <Upload size={16} />
                {editingShortId ? 'Update Now' : 'Publish Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Editor;
